// EdgeCasesSection.tsx
import React, { useState } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Spinner } from "../ui/spinner";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { generateEdgeCases } from "@/lib/aiPhoneAgent";
import { Plus } from "lucide-react";

interface EdgeCasesSectionProps {
  mainSystemPrompt: string;
  strategyPrompt: string;
  onEdgeCasesGenerated?: (edgeCases: string[]) => void;
  onAddToSimulation: (edgeCase: string) => void;
  onReset: () => void;
  onLoadingChange?: (loading: boolean) => void;
  edgeCases: string[];
}

export const EdgeCasesSection = ({
  mainSystemPrompt,
  strategyPrompt,
  onEdgeCasesGenerated,
  onAddToSimulation,
  onReset,
  edgeCases,
}: EdgeCasesSectionProps) => {
  const [streamingCases, setStreamingCases] = useState<string[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleGenerateEdgeCases = async () => {
    try {
      setError("");
      setLoading(true);
      setStreamingCases([]);

      const fullResponsePromise = new Promise<string[]>((resolve, reject) => {
        generateEdgeCases(mainSystemPrompt, strategyPrompt, (currentCases) => {
          setStreamingCases(currentCases);
        })
          .then(resolve)
          .catch(reject);
      });

      const newEdgeCases = await fullResponsePromise;
      const baseCase =
        "A potential conversation flow including normal questions, confusion and concerns from the patient.";
      const updatedEdgeCases = [baseCase, ...newEdgeCases];

      onEdgeCasesGenerated?.(updatedEdgeCases);
      setStreamingCases([]);
    } catch (error) {
      console.error("Error:", error);
      setError("Failed to generate edge cases. Please try again.");
      setStreamingCases([]);
    } finally {
      setLoading(false);
    }
  };

  if (!mainSystemPrompt || !strategyPrompt) return null;

  const displayCases = loading ? streamingCases : edgeCases;

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="flex justify-between items-center">
          Edge Cases
          <div className="flex gap-2">
            <Button
              onClick={handleGenerateEdgeCases}
              disabled={loading}
              variant="outline"
              size="sm"
            >
              {loading ? <Spinner /> : "Generate Edge Cases"}
            </Button>
            {edgeCases.length > 0 && (
              <Button onClick={onReset} variant="outline" size="sm">
                Reset Edge Cases
              </Button>
            )}
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {displayCases.length > 0 && (
          <ul className="space-y-2">
            {displayCases.map((edgeCase, index) => (
              <li key={index} className="p-4 bg-slate-50 rounded-md text-sm">
                <div className="flex justify-between items-center">
                  <span>{edgeCase}</span>
                  <Button
                    onClick={() => onAddToSimulation(edgeCase)}
                    size="sm"
                    variant="outline"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add to Simulations
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};
